import { dot, type Vec3 } from '@rcs/domain';
import type { SimulationResult } from './simulate';

/** 走行結果の統計量（docs/physics/physics-model.md 10 章）。距離は m、速度は m/s、角度は rad、G は G。 */
export interface RideStatistics {
  /** 所要時間 [s] */
  readonly duration: number;
  /** 走行距離 [m]（後退も距離に含める） */
  readonly distance: number;
  readonly maxSpeed: number;
  /** 平均速度 [m/s]（走行距離 ÷ 所要時間） */
  readonly averageSpeed: number;
  readonly maxHeight: number;
  readonly minHeight: number;
  /** 最大落差 [m]：走行中の最高点からの、その後の最大の下がり幅 */
  readonly maxDrop: number;
  /** 最大勾配 [rad]（水平面との角度の絶対値の最大） */
  readonly maxGradient: number;
  readonly maxVerticalG: number;
  readonly minVerticalG: number;
  readonly maxLateralG: number;
  readonly minLateralG: number;
  readonly maxLongitudinalG: number;
  readonly minLongitudinalG: number;
  /** 上方向が下を向いた状態（反転）へ入った回数 */
  readonly inversions: number;
  /** 上下 G が負（浮き上がり）だった時間の合計 [s] */
  readonly airtime: number;
}

const WORLD_UP: Vec3 = [0, 1, 0];

/** シミュレーション結果から統計量を算出する。結果のフレームが空の場合は 0 を返す。 */
export function summarize(result: SimulationResult): RideStatistics {
  const frames = result.frames;
  const first = frames[0];
  if (first === undefined) {
    return {
      duration: 0,
      distance: 0,
      maxSpeed: 0,
      averageSpeed: 0,
      maxHeight: 0,
      minHeight: 0,
      maxDrop: 0,
      maxGradient: 0,
      maxVerticalG: 0,
      minVerticalG: 0,
      maxLateralG: 0,
      minLateralG: 0,
      maxLongitudinalG: 0,
      minLongitudinalG: 0,
      inversions: 0,
      airtime: 0,
    };
  }
  let distance = 0;
  let maxSpeed = 0;
  let maxHeight = first.position[1];
  let minHeight = first.position[1];
  let maxDrop = 0;
  let maxGradient = 0;
  let maxV = first.gForce.vertical;
  let minV = first.gForce.vertical;
  let maxL = first.gForce.lateral;
  let minL = first.gForce.lateral;
  let maxF = first.gForce.longitudinal;
  let minF = first.gForce.longitudinal;
  let inversions = 0;
  let airtime = 0;
  let runningTop = first.position[1];
  let inverted = dot(first.orientation.up, WORLD_UP) < 0;
  let previous = first;
  for (const frame of frames) {
    const height = frame.position[1];
    distance += Math.abs(frame.s - previous.s);
    maxSpeed = Math.max(maxSpeed, Math.abs(frame.speed));
    maxHeight = Math.max(maxHeight, height);
    minHeight = Math.min(minHeight, height);
    runningTop = Math.max(runningTop, height);
    maxDrop = Math.max(maxDrop, runningTop - height);
    maxGradient = Math.max(
      maxGradient,
      Math.asin(Math.min(1, Math.abs(frame.orientation.tangent[1]))),
    );
    const { vertical, lateral, longitudinal } = frame.gForce;
    maxV = Math.max(maxV, vertical);
    minV = Math.min(minV, vertical);
    maxL = Math.max(maxL, lateral);
    minL = Math.min(minL, lateral);
    maxF = Math.max(maxF, longitudinal);
    minF = Math.min(minF, longitudinal);
    const nowInverted = dot(frame.orientation.up, WORLD_UP) < 0;
    if (nowInverted && !inverted) inversions += 1;
    inverted = nowInverted;
    if (vertical < 0) airtime += frame.time - previous.time;
    previous = frame;
  }
  const duration = result.meta.duration;
  return {
    duration,
    distance,
    maxSpeed,
    averageSpeed: duration > 0 ? distance / duration : 0,
    maxHeight,
    minHeight,
    maxDrop,
    maxGradient,
    maxVerticalG: maxV,
    minVerticalG: minV,
    maxLateralG: maxL,
    minLateralG: minL,
    maxLongitudinalG: maxF,
    minLongitudinalG: minF,
    inversions,
    airtime,
  };
}
